import type {
  ControlBridgeStructureNode,
  KryspaceControlBridgeStructure,
} from '../../types/kryspaceControlBridgeStructure';
import { normalizeScopeIdKey, scopeIdsEqual } from '../../utils/scopeIdPlane';
import { scopeKeys, type ScopeContext } from './filters';

function nodeScopeId(node: ControlBridgeStructureNode): string {
  return node.filterScope ?? node.key;
}

/** Nested descendants only (storefront under store under domain). Drive envelope, not Diagnose. */
function descendantScopeIds(node: ControlBridgeStructureNode): string[] {
  const out: string[] = [];
  for (const child of node.children ?? []) {
    const id = nodeScopeId(child);
    if (id) out.push(id);
    out.push(...descendantScopeIds(child));
  }
  return out;
}

function findNode(
  node: ControlBridgeStructureNode,
  scope: string
): ControlBridgeStructureNode | null {
  if (scopeIdsEqual(nodeScopeId(node), scope)) {
    return node;
  }
  for (const child of node.children ?? []) {
    const hit = findNode(child, scope);
    if (hit) return hit;
  }
  return null;
}

function findNodeLabel(node: ControlBridgeStructureNode, scope: string): string | null {
  return findNode(node, scope)?.label ?? null;
}

/**
 * Diagnose / passesFilter: direct children only (one rail hop), including nested
 * nodes so selecting a mid-level lane still sees its own children.
 * Nested storefronts are not children of the grandparent domain.
 */
export function buildScopeContext(structure: KryspaceControlBridgeStructure): ScopeContext {
  const childrenByScope: Record<string, string[]> = {};
  const businessAreaScopes = new Set<string>();

  const indexNode = (node: ControlBridgeStructureNode) => {
    const scopeKey = nodeScopeId(node);
    if (node.role === 'subdomain' && scopeKey) {
      businessAreaScopes.add(scopeKey);
    }
    if (node.children?.length && scopeKey) {
      childrenByScope[scopeKey] = node.children.map((child) => nodeScopeId(child)).filter(Boolean);
      for (const child of node.children) {
        indexNode(child);
      }
    }
  };

  for (const group of structure.groups) {
    for (const node of group.nodes) {
      indexNode(node);
    }
  }

  return { childrenByScope, businessAreaScopes };
}

/** Drive FlowEnvelope: selected node plus all nested descendants, then Diagnose one-hop keys. */
export function expandDriveEnvelopeScopeKeys(
  scope: string,
  structure: KryspaceControlBridgeStructure,
  baseKeys: string[]
): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  const push = (id: string) => {
    const trimmed = id.trim();
    const key = normalizeScopeIdKey(trimmed) || trimmed;
    if (!trimmed || seen.has(key)) return;
    seen.add(key);
    out.push(trimmed);
  };
  for (const key of baseKeys) push(key);
  if (!scope || scope === 'company') return out;
  for (const group of structure.groups) {
    for (const node of group.nodes) {
      const hit = findNode(node, scope);
      if (!hit) continue;
      push(nodeScopeId(hit));
      for (const id of descendantScopeIds(hit)) push(id);
      return out;
    }
  }
  return out;
}

/**
 * Drive overlay queues: Diagnose one-hop context plus nested descendants of the
 * selected rail, so storefront occupancy matches FlowEnvelope expand.
 */
export function driveScopeContextForRail(
  scope: string,
  structure: KryspaceControlBridgeStructure,
  diagnoseContext: ScopeContext
): ScopeContext {
  const keys = expandDriveEnvelopeScopeKeys(scope, structure, scopeKeys(scope, diagnoseContext));
  if (!scope || scope === 'company' || keys.length <= 1) {
    return diagnoseContext;
  }
  const children = keys.filter((id) => !scopeIdsEqual(id, scope));
  return {
    ...diagnoseContext,
    childrenByScope: {
      ...diagnoseContext.childrenByScope,
      [scope]: children,
    },
  };
}

export function resolveScopeLabel(scope: string, structure: KryspaceControlBridgeStructure): string {
  if (!scope || scope === 'company') {
    return 'Company-wide';
  }
  for (const group of structure.groups) {
    for (const node of group.nodes) {
      const hit = findNodeLabel(node, scope);
      if (hit) return hit;
    }
  }
  return scope;
}
